import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import {
  ApiHolder,
  configApiRef,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { AuthorizeResult } from '@backstage/plugin-permission-common';
import { permissionApiRef } from '@backstage/plugin-permission-react';
import {
  STAGES,
  Stage,
  keyVaultAnnotation,
  keyvaultSecretWritePermission,
} from '@internal/backstage-plugin-keyvault-secrets-common';
import { KeyVaultSecretsClient } from './api';

export function secretStages(entity: Entity): Stage[] {
  const annotations = entity.metadata.annotations ?? {};
  return STAGES.filter(stage => Boolean(annotations[keyVaultAnnotation(stage)]));
}

async function isAllowed(
  entityRef: string,
  context: { apis: ApiHolder },
): Promise<boolean> {
  const permissionApi = context.apis.get(permissionApiRef);
  const discoveryApi = context.apis.get(discoveryApiRef);
  const fetchApi = context.apis.get(fetchApiRef);
  if (!permissionApi || !discoveryApi || !fetchApi) {
    return false;
  }
  const decision = await permissionApi.authorize({
    permission: keyvaultSecretWritePermission,
  });
  if (decision.result !== AuthorizeResult.ALLOW) {
    return false;
  }
  return new KeyVaultSecretsClient(discoveryApi, fetchApi).isAllowed(entityRef);
}

/**
 * An `EntitySwitch.Case` predicate. The config flag and the annotation are
 * checked synchronously. The permission is checked before `GET /access`, so
 * a page view by a user without it does not reach the plugin's audited route.
 */
export function isKeyVaultSecretsAvailable(
  entity: Entity,
  context: { apis: ApiHolder },
): boolean | Promise<boolean> {
  const config = context.apis.get(configApiRef);
  if (!config?.getOptionalBoolean('keyvaultSecrets.enabled')) {
    return false;
  }
  if (secretStages(entity).length === 0) {
    return false;
  }
  return isAllowed(stringifyEntityRef(entity), context).catch(() => false);
}
